export interface AvailabilityException {
  /**
   * Availability exception ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the availability exception is updated.
   * To prevent conflicting changes, the current revision must be specified when updating the availability exception.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.
   * @readonly
   */
  updatedDate?: Date | null;
  /** The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  startTime?: Date | null;
  /** The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format. */
  endTime?: Date | null;
  /**
   * Whether the exception makes the [`start_time`, `end_time`] range available.
   * If `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.
   * Currently, only `false` is supported.
   * @readonly
   */
  available?: boolean | null;
  /**
   * Exception name.
   * @maxLength 500
   */
  name?: string | null;
  /** Fulfillment methods for which this exception applies. */
  affectedFulfillmentMethods?: AffectedFulfillmentMethods;
  /**
   * ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)
   * @format GUID
   */
  operationId?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.
   * @format GUID
   * @readonly
   */
  businessLocationId?: string | null;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions. */
  tags?: Tags;
}

export interface AffectedFulfillmentMethods
  extends AffectedFulfillmentMethodsAffectedMethodsOptionsOneOf {
  /** Settings for specific fulfillment methods. */
  specificFulfillmentMethodsOptions?: SpecificFulfillmentMethodsImpactScope;
  /** The type of the fulfillment methods this exception applies to. */
  affectedMethods?: AffectedMethodsWithLiterals;
}

/** @oneof */
export interface AffectedFulfillmentMethodsAffectedMethodsOptionsOneOf {
  /** Settings for specific fulfillment methods. */
  specificFulfillmentMethodsOptions?: SpecificFulfillmentMethodsImpactScope;
}

/** The scope the exception applied. */
export enum AffectedMethods {
  /** All fulfillment methods of this operation applied for this exception. */
  ALL_FULFILLMENT_METHODS = 'ALL_FULFILLMENT_METHODS',
  /** Specific fulfillment methods applied for this exception. */
  SPECIFIC_FULFILLMENT_METHODS = 'SPECIFIC_FULFILLMENT_METHODS',
  /** All pickup fulfillment methods applied for this exception. */
  ALL_PICKUP_FULFILLMENT_METHODS = 'ALL_PICKUP_FULFILLMENT_METHODS',
  /** All delivery fulfillment methods applied for this exception. */
  ALL_DELIVERY_FULFILLMENT_METHODS = 'ALL_DELIVERY_FULFILLMENT_METHODS',
}

/** @enumType */
export type AffectedMethodsWithLiterals =
  | AffectedMethods
  | 'ALL_FULFILLMENT_METHODS'
  | 'SPECIFIC_FULFILLMENT_METHODS'
  | 'ALL_PICKUP_FULFILLMENT_METHODS'
  | 'ALL_DELIVERY_FULFILLMENT_METHODS';

/** Details of the specific fulfillment methods this exception applies to. */
export interface SpecificFulfillmentMethodsImpactScope {
  /**
   * IDs of the fulfillment methods this exception applies to.
   * @format GUID
   * @maxSize 500
   */
  fulfillmentMethodIds?: string[] | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export enum Type {
  /** Unspecified Availability exception type. */
  UNSPECIFIED_AVAILABILITY_EXCEPTION_TYPE = 'UNSPECIFIED_AVAILABILITY_EXCEPTION_TYPE',
  /** Ordering paused. */
  ORDERING_PAUSED = 'ORDERING_PAUSED',
  /** The restaurant may be closed completely or only partially. For example, a restaurant may be closed only for pickup orders, only for delivery orders, or only for orders from a specific delivery area. */
  CLOSURE = 'CLOSURE',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNSPECIFIED_AVAILABILITY_EXCEPTION_TYPE'
  | 'ORDERING_PAUSED'
  | 'CLOSURE';

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface CreateAvailabilityExceptionRequest {
  /** Availability exception details. */
  availabilityException: AvailabilityException;
}

export interface CreateAvailabilityExceptionResponse {
  /** The created availability exception. */
  availabilityException?: AvailabilityException;
}

export interface GetAvailabilityExceptionRequest {
  /**
   * ID of the availability exception to retrieve.
   * @format GUID
   */
  availabilityExceptionId: string;
}

export interface GetAvailabilityExceptionResponse {
  /** The requested availability exception. */
  availabilityException?: AvailabilityException;
}

export interface UpdateAvailabilityExceptionRequest {
  /** Availability exception to update. */
  availabilityException: AvailabilityException;
}

export interface UpdateAvailabilityExceptionResponse {
  /** Updated availability exception. */
  availabilityException?: AvailabilityException;
}

export interface DeleteAvailabilityExceptionRequest {
  /**
   * ID of the availability exception to delete.
   * @format GUID
   */
  availabilityExceptionId: string;
}

export interface DeleteAvailabilityExceptionResponse {}

export interface QueryAvailabilityExceptionsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 64
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAvailabilityExceptionsResponse {
  /** List of availability exceptions. */
  availabilityExceptions?: AvailabilityException[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkCreateAvailabilityExceptionsRequest {
  /**
   * Availability exception details.
   * @minSize 1
   * @maxSize 20
   */
  availabilityExceptions: AvailabilityException[];
  /** Whether the full availability exception entity is returned. Defaults to `true`. */
  returnEntity?: boolean;
}

export interface BulkCreateAvailabilityExceptionsResponse {
  /** Information about the created availability exception. */
  results?: BulkCreateAvailabilityExceptionsResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateAvailabilityExceptionsResult {
  /** Metadata for availability exception creation. */
  itemMetadata?: ItemMetadata;
  /** Created availability exception. */
  availabilityException?: AvailabilityException;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateAvailabilityExceptionsRequest {
  /**
   * Availability exceptions entities to update.
   * @minSize 1
   * @maxSize 100
   */
  availabilityExceptions: MaskedAvailabilityException[];
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

export interface MaskedAvailabilityException {
  /** Availability exception entity to update. */
  availabilityException?: AvailabilityException;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateAvailabilityExceptionsResponse {
  /** Information about the updated availability exceptions. */
  results?: BulkAvailabilityExceptionResult[];
  /** Bulk Update Availability Exceptions metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkAvailabilityExceptionResult {
  /** Metadata of the availability exception update. */
  itemMetadata?: ItemMetadata;
  /** Availability exception (present when return_entity is true). */
  item?: AvailabilityException;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface BulkUpdateAvailabilityExceptionTagsRequest {
  /**
   * IDs of the availability exceptions to update tags for.
   * @format GUID
   * @maxSize 100
   */
  availabilityExceptionIds: string[];
  /** Tags to assign to the availability exceptions. */
  assignTags?: Tags;
  /** Tags to unassign from the availability exceptions. */
  unassignTags?: Tags;
}

export interface BulkUpdateAvailabilityExceptionTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateAvailabilityExceptionTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateAvailabilityExceptionTagsResult {
  /** Metadata for the updated availability exception. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateAvailabilityExceptionTagsByFilterRequest {
  /** Filter that determines which availability exceptions to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the availability exceptions. */
  assignTags?: Tags;
  /** Tags to unassign from the availability exceptions. */
  unassignTags?: Tags;
}

export interface BulkUpdateAvailabilityExceptionTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateAvailabilityExceptionTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateAvailabilityExceptionTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
